/**
 * 福岡県の背景ツアー「福岡めぐり」コンテンツ。
 *
 * ロジックから分離したデータ。新しい都道府県を追加する場合は、
 * 同じインターフェースで StageDefinition.scenery.tour に差し込むだけ。
 */

import type { StageTour } from '../types';

export const fukuokaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'yanagawa',
      name: '柳川の川下り',
      area: '柳川市・朝',
      image: '/assets/scenery/fukuoka/scenes/02_yanagawa.webp',
      hotspots: [
        {
          id: 'yanagawa-donko',
          x: 0.68,
          y: 0.42,
          title: 'どんこ舟',
          body: '船頭さんが竿一本で操る舟に乗って、城下町の堀割をゆったりめぐります。低い橋の下では、みんなで身をかがめてくぐるのもお約束。',
          source: 'https://www.yanagawa-net.com/features/5654/',
          mapQuery: '柳川 川下り',
        },
        {
          id: 'yanagawa-unagi',
          x: 0.38,
          y: 0.37,
          title: '柳川の名物',
          body: '柳川に来たら「うなぎのせいろ蒸し」。タレをまぶしたご飯にうなぎの蒲焼と錦糸卵をのせ、せいろで蒸し上げます。文化庁の「100年フード」にも選ばれた郷土料理です。',
          source:
            'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/hyakunenfood/jirei/list_fukuoka.html',
          mapQuery: '柳川 うなぎのせいろ蒸し',
        },
      ],
    },
    {
      id: 'dazaifu',
      name: '太宰府天満宮',
      area: '太宰府市・午前',
      image: '/assets/scenery/fukuoka/scenes/03_dazaifu.webp',
      hotspots: [
        {
          id: 'dazaifu-romon',
          x: 0.51,
          y: 0.2,
          title: '楼門',
          body: '学問の神様・菅原道真公をまつる天満宮。本殿へ向かう途中にくぐる二階建ての楼門が目印で、年間約1000万人が参拝に訪れます。',
          source: 'https://www.city.dazaifu.lg.jp/site/kanko/11679.html',
          mapQuery: '太宰府天満宮',
        },
        {
          id: 'dazaifu-umegae',
          x: 0.5,
          y: 0.46,
          title: '参道の梅ヶ枝餅',
          body: '参道には焼きたての梅ヶ枝餅の店がずらり。毎月17日は「きゅーはくの日」で古代米入り、25日は「天神様の日」でよもぎ入りの限定版が並びます。',
          source: 'https://dazaifu.org/dazaifudayori/dayori2972/',
          relatedItemId: 'umegae-mochi',
          mapQuery: '太宰府 参道 梅ヶ枝餅',
        },
      ],
    },
    {
      id: 'ohori',
      name: '大濠公園',
      area: '福岡市中央区・昼',
      image: '/assets/scenery/fukuoka/scenes/04_ohori.webp',
      hotspots: [
        {
          id: 'ohori-ukimido',
          x: 0.2,
          y: 0.34,
          title: '浮見堂',
          body: '池の中の島にたたずむ、朱色のお堂。大濠公園は福岡城の外堀を生かして造られた、水辺の公園です。',
          source: 'https://www.crossroadfukuoka.jp/course/ohoripark',
          mapQuery: '大濠公園 浮見堂',
        },
        {
          id: 'ohori-lake',
          x: 0.8,
          y: 0.36,
          title: '池をひとめぐり',
          body: '池のまわりは約2kmの周遊道。ジョギングや散歩を楽しむ人でいつもにぎわっています。',
          source: 'https://www.ohorikouen.jp/about/',
          mapQuery: '大濠公園',
        },
      ],
    },
    {
      id: 'nokonoshima',
      name: '能古島',
      area: '福岡市西区・昼過ぎ',
      image: '/assets/scenery/fukuoka/scenes/05_nokonoshima.webp',
      hotspots: [
        {
          id: 'noko-flowers',
          x: 0.35,
          y: 0.4,
          title: '花の丘',
          body: '博多湾に浮かぶ島。春は菜の花、秋はコスモスが丘一面に咲き、花ごしに志賀島から海の中道まで博多湾を一望できます。',
          source: 'https://www.crossroadfukuoka.jp/spot/12340',
          mapQuery: 'のこのしまアイランドパーク',
        },
        {
          id: 'noko-ferry',
          x: 0.62,
          y: 0.22,
          title: '渡船で約10分',
          body: '姪浜の渡船場から船でわずか10分ほど。街からすぐの、気軽な島旅スポットです。',
          source: 'https://www.city.fukuoka.lg.jp/kowan/kyakusen/hakata-port/hyo.html',
          mapQuery: '能古島渡船場',
        },
      ],
    },
    {
      id: 'kokura',
      name: '小倉城',
      area: '北九州市・午後',
      image: '/assets/scenery/fukuoka/scenes/06_kokura.webp',
      hotspots: [
        {
          id: 'kokura-castle',
          x: 0.58,
          y: 0.28,
          title: '唐造りの天守',
          body: '最上階が下の階より大きく張り出した、全国でもめずらしい「唐造り」の天守。福岡県で唯一、天守閣を持つ城です。',
          source: 'https://kokura-castle.jp/castle/',
          mapQuery: '小倉城',
        },
        {
          id: 'kokura-yakiudon',
          x: 0.12,
          y: 0.22,
          title: '小倉生まれの焼うどん',
          body: '小倉で生まれたとされる焼うどん。終戦直後、焼そばのそば玉が手に入りにくく、干しうどんで代用したのが始まりといわれ、文化庁の「100年フード」にも認定されています。',
          source:
            'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/hyakunenfood/jirei/list_fukuoka.html',
          mapQuery: '小倉 焼うどん',
        },
      ],
    },
    {
      id: 'mojiko',
      name: '門司港レトロ',
      area: '北九州市・夕方',
      image: '/assets/scenery/fukuoka/scenes/07_mojiko.webp',
      hotspots: [
        {
          id: 'mojiko-station',
          x: 0.3,
          y: 0.34,
          title: '大正ロマンの街並み',
          body: '門司港駅の駅舎は1914年の建築で、駅として初めて国の重要文化財に指定されました。現役の駅舎で重要文化財は、門司港駅と東京駅だけです。',
          source: 'https://www.city.kitakyushu.lg.jp/moji/w1100456.html',
          mapQuery: '門司港駅',
        },
        {
          id: 'mojiko-bridge',
          x: 0.72,
          y: 0.3,
          title: '関門橋',
          body: '関門海峡の下には、歩いて渡れる全長780mの人道トンネルがあり、その中ほどに福岡県と山口県の県境があります。門司側の入口あたりからは関門橋の景観も楽しめます。',
          source: 'https://www.crossroadfukuoka.jp/spot/11798',
          mapQuery: '関門トンネル人道',
        },
        {
          id: 'mojiko-curry',
          x: 0.55,
          y: 0.46,
          title: '焼きカレーの港町',
          body: '門司港の名物は、ご飯の上にカレーとチーズや卵をのせ、オーブンで焼いた「焼きカレー」。香ばしさが持ち味で、店ごとに工夫をこらした一皿が楽しめます。',
          source: 'https://www.crossroadfukuoka.jp/spot/10415',
          relatedItemId: 'yaki-curry',
          mapQuery: '門司港 焼きカレー',
        },
      ],
    },
    {
      id: 'itoshima',
      name: '桜井二見ヶ浦',
      area: '糸島市・日の入り',
      image: '/assets/scenery/fukuoka/scenes/01_itoshima.webp',
      hotspots: [
        {
          id: 'itoshima-meoto',
          x: 0.3,
          y: 0.34,
          title: '夫婦岩',
          body: 'しめ縄で結ばれた2つの岩が並ぶ、糸島を代表する景色。長さ30m・重さ約1tの大注連縄は毎年春に掛け替えられ、夏至のころには岩の間に夕日が沈みます。',
          source: 'https://kanko-itoshima.jp/spot/sakuraihutamigaura/',
          mapQuery: '桜井二見ヶ浦 夫婦岩',
        },
        {
          id: 'itoshima-torii',
          x: 0.74,
          y: 0.27,
          title: '海に立つ白い鳥居',
          body: '波打ち際に立つ白い大鳥居と夫婦岩の組み合わせが名物。「日本の渚百選」にも選ばれた、夕日の名所です。',
          source: 'https://www.crossroadfukuoka.jp/spot/12456',
          mapQuery: '二見ヶ浦 白い鳥居',
        },
      ],
    },
    {
      id: 'momochi',
      name: 'シーサイドももち',
      area: '福岡市早良区・日没後',
      image: '/assets/scenery/fukuoka/scenes/08_momochi.webp',
      hotspots: [
        {
          id: 'momochi-tower',
          x: 0.5,
          y: 0.25,
          title: '福岡タワー',
          body: '全長234m、海浜タワーとしては日本一の高さ。外壁をおおう8000枚のハーフミラーが空の色を映します。',
          source: 'https://www.fukuokatower.co.jp/company/data.php',
          mapQuery: '福岡タワー',
        },
        {
          id: 'momochi-beach',
          x: 0.25,
          y: 0.45,
          title: 'ももち浜',
          body: '街のすぐそばにある人工の砂浜。ビーチバレーなどビーチスポーツのメッカで、背後のビル群と海の風景が美しく、昼も夜もにぎわいます。',
          source: 'https://www.city.fukuoka.lg.jp/kowan/miryoku/fu-kei/seasidemomochi.html',
          mapQuery: 'シーサイドももち海浜公園',
        },
      ],
    },
    {
      id: 'kushida',
      name: '櫛田神社',
      area: '福岡市博多区・夜',
      image: '/assets/scenery/fukuoka/scenes/09_kushida.webp',
      hotspots: [
        {
          id: 'kushida-kazariyama',
          x: 0.43,
          y: 0.28,
          title: '飾り山笠',
          body: '博多の総鎮守。境内には高さ13mもある豪華な飾り山笠が、6月を除いて一年中展示されています。',
          source: 'https://yokanavi.com/spots/26906',
          mapQuery: '櫛田神社 飾り山',
        },
        {
          id: 'kushida-oiyama',
          x: 0.08,
          y: 0.33,
          title: '追い山笠',
          body: '博多祇園山笠のクライマックス「追い山笠」は、7月15日の午前4時59分に櫛田神社からスタートし、約5kmを駆け抜けます。',
          source: 'https://www.hakatayamakasa.com/162358.html',
          mapQuery: '櫛田神社',
        },
      ],
    },
    {
      id: 'nakasu',
      name: '中洲の屋台',
      area: '福岡市博多区・深夜',
      image: '/assets/scenery/fukuoka/scenes/10_nakasu.webp',
      hotspots: [
        {
          id: 'nakasu-yatai',
          x: 0.25,
          y: 0.44,
          title: '中洲の屋台',
          body: '福岡の屋台は1960年代のピーク時に400軒以上、いまも100軒以上が軒を構える日本最大級の屋台スポット。中洲には昔ながらの屋台が並び、隣り合ったお客さんや店主との会話も楽しみのひとつです。',
          source: 'https://www.crossroadfukuoka.jp/feature/yatai',
          mapQuery: '中洲 屋台',
        },
        {
          id: 'nakasu-shime',
          x: 0.8,
          y: 0.38,
          title: '席はゆずりあい',
          body: '屋台では、席は譲り合いが基本。食べ終えたら長居は控えて、次のお客さんに席をゆずるのがマナーです。',
          source: 'https://www.crossroadfukuoka.jp/feature/yatai',
          relatedItemId: 'hakata-tonkotsu-ramen',
          mapQuery: '中洲 屋台 ラーメン',
        },
      ],
    },
  ],
};
